import {
    Show,
    useRecordContext,
    useGetList,
    useTranslate,
    Labeled,
    Loading,
    useRefresh,
    useLocaleState
} from "react-admin";
import {
    Card, CardContent, Typography, Box, useMediaQuery, StepContent,
    Theme, CardActions, ButtonGroup, Stepper, Step, StepLabel, Button, CardHeader
} from "@mui/material";
import {useDefineAppLocation} from "@react-admin/ra-navigation";
import {OrderReturn} from "../../types";
import * as React from "react";
import {returnStates} from './returnStates';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import EditIcon from '@mui/icons-material/Edit';
import AutoRenewIcon from '@mui/icons-material/Autorenew';
import {Helmet} from 'react-helmet';
import IosShareIcon from '@mui/icons-material/IosShare';
import OrderShow from "./OrderShow";
import OrderReturnDetails from "./OrderReturnDetails";
import OrderReturnItems from "./OrderReturnItems";
import OrderReturnLabels from "./OrderReturnLabels";
import DefaultRequestDialog from "./dialog/DefaultRequestDialog";
import DeclineRequestDialog from "./dialog/DeclineRequestDialog";
import ClarificationRequestDialog from "./dialog/ClarificationRequestDialog";
import {createElement} from "react";
import Tooltip from '@mui/material/Tooltip';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DescriptionIcon from '@mui/icons-material/Description';
import EditShippingInformationDialog from "./dialog/EditShippingInformationDialog";
import PackageLostRequestDialog from "./dialog/PackageLostRequestDialog";

export type Transition = {
    id: string;
    target: string;
};

const Aside = () => {
    const record = useRecordContext<OrderReturn>();
    useDefineAppLocation('sales.order_returns.order_return_show', {record});

    return (
        <Box width={{xs: "100%", md: 300, lg: 400}} ml={{md: 2}} mt={{xs: 2, md: 0}}
             sx={{minWidth: 300, displayPrint: 'none'}}>
            {record && <StatusPanel record={record}/>}
            {/*{record && <ShippingDataForReturn record={record}/>}*/}
        </Box>
    );
};

const ShippingDataForReturn = (props: { record: OrderReturn }) => {
    const record = props.record;
    const t = useTranslate();

    return (
        <Box width={{xs: "100%", md: 300, lg: 400}} ml={{md: 0}} mt={{xs: 2}} sx={{minWidth: 300}}>
            <Card>
                <CardHeader
                    action={
                        record.isAccepted ? null : <IconButton aria-label="settings">
                            <EditShippingInformationDialog record={record}/>
                        </IconButton>
                    }
                    title={t('resources.order.return.shippingOptionsHeadline')}
                />
                <CardContent>
                    <Card
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '1',
                            marginBottom: '10px',
                            '& a': {
                                textDecoration: 'none',
                                color: 'inherit',
                            },
                        }}
                    >
                        <Tooltip title={t('resources.order.return.fields.carrier')}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    padding: '10px 15px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '& .icon': {
                                        color: `primary.main`,
                                    }
                                }}
                            >
                                <Box width="3em" className="icon">
                                    {createElement(LocalShippingIcon, {fontSize: 'small'})}
                                </Box>
                                <Box textAlign="right">
                                    <Typography variant="subtitle1" component="h2">
                                        {record?.carrier ?? 'n/a'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>
                    </Card>
                    <Card
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: '1',
                            marginBottom: '10px',
                            '& a': {
                                textDecoration: 'none',
                                color: 'inherit',
                            },
                        }}
                    >
                        <Tooltip title={t('resources.order.return.fields.label')}>
                            <Box
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    padding: '10px 15px',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    '& .icon': {
                                        color: `primary.main`,
                                    }
                                }}
                            >
                                <Box width="3em" className="icon">
                                    {createElement(DescriptionIcon, {fontSize: 'small'})}
                                </Box>
                                <Box textAlign="right">
                                    <Typography variant="subtitle1" component="h2">
                                        {record?.label ?
                                            <Link href={record?.label} sx={{textDecoration: 'underline !important;'}}>
                                                {t('ra.action.download')}
                                            </Link>
                                            : 'n/a'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Tooltip>
                    </Card>
                </CardContent>
                {record?.labelHelperUrl ?
                    <CardActions sx={{display: 'flex', flexDirection: 'column', width: '100%'}}>
                        <Typography variant="caption" color="textSecondary" width="100%" sx={{textAlign: 'center'}}>
                            {t('resources.order.return.labelHelperLinkTip')}
                        </Typography>
                        <Link href={record.labelHelperUrl} underline="always" target="_blank" rel="noopener noreferrer" sx={{textAlign: 'center', width: '100%'}}>
                            {t('resources.order.return.labelHelperLink')}
                        </Link>
                    </CardActions> : null
                }
            </Card>
        </Box>
    );
};

const CustomHelmet = () => {
    const record = useRecordContext<OrderReturn>();

    if (!record) {
        return null;
    }

    return (
        <Helmet>
            <title>
                Order return: {record.number}
            </title>
        </Helmet>
    );
}


const HistoryComponent = (props: { record: OrderReturn }) => {
    const record = props.record;
    const translate = useTranslate();
    const [locale, setLocale] = useLocaleState();

    const [activeStep, setActiveStep] = React.useState(0);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
    };

    const {data: list, isLoading} = useGetList(`order-returns-events`, {
            filter: {orderReturn: record.id},
            sort: {field: 'id', order: 'ASC'},
            pagination: {page: 1, perPage: 999}
        },
        {staleTime: 30000}
    );

    const types = {
        CREATE: {
            icon: FiberNewIcon,
            label: 'resources.order.return.history.created'
        },
        STATUS_UPDATE: {
            icon: AutoRenewIcon,
            label: 'resources.order.return.history.statusUpdate'
        },
        UPDATE: {
            icon: EditIcon,
            label: 'resources.order.return.history.dataUpdate'
        },
        DATA_SEND_TO_SL: {
            icon: IosShareIcon,
            label: 'resources.order.return.history.slDataSend'
        },
    };

    if (isLoading) {
        return <Loading/>;
    }

    return (
        <Box width={{xs: "100%", md: 300, lg: 400}} ml={{md: 0}} mt={2} sx={{minWidth: 300}}>
            {list?.length ?
                <Card>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {translate('resources.order.return.historyHeadline')}
                        </Typography>
                        <Stepper activeStep={activeStep} orientation="vertical" sx={{mt: 1}}>
                            {list?.map((step: any, index) => {
                                const type = types[step.eventType] ?? types["UPDATE"];
                                const data = Object.entries(step.newData).map(([key, value]) => {
                                    if (value && typeof value === 'object') {
                                        value = JSON.stringify(value, null, 2)
                                    } else if (value && typeof value === 'string') {
                                        value = value.length > 54 ? value.substring(0, 54) + '...' : value;
                                    }

                                    return `${key}: ${value}`;
                                });

                                const time = new Date(step.eventTime).toLocaleString(locale, {
                                    year: 'numeric',
                                    month: 'numeric',
                                    day: 'numeric',
                                    hour: 'numeric',
                                    minute: 'numeric',
                                });
                                return (
                                    <Step
                                        key={`${step.id}-cu_step`}
                                        sx={{
                                            '& .MuiStepLabel-labelContainer': {
                                                fontSize: '12px',
                                            },
                                        }}
                                    >
                                        <StepLabel
                                            optional={`(${time} ${translate('resources.order.return.history.by')} ${step.authorName})`}
                                        >
                                            {translate(type.label)}
                                        </StepLabel>
                                        <StepContent>
                                            {
                                                data.map((d) =>
                                                    <Typography variant="caption" color="text.secondary"
                                                                component={"p"}>
                                                        {d}
                                                    </Typography>)
                                            }
                                            <Box sx={{mb: 2}}>
                                                <Button
                                                    size={"small"}
                                                    variant="contained"
                                                    onClick={handleNext}
                                                    sx={{
                                                        mt: 1,
                                                        mr: 1,
                                                        display: index === list.length - 1 ? 'none' : 'inline-flex'
                                                    }}
                                                >
                                                    {translate('resources.order.return.history.next')}
                                                </Button>
                                                <Button
                                                    size={"small"}
                                                    onClick={handleBack}
                                                    sx={{mt: 1, mr: 1, display: index === 0 ? 'none' : 'inline-flex'}}
                                                >
                                                    {translate('resources.order.return.history.previous')}
                                                </Button>
                                            </Box>
                                        </StepContent>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </CardContent>
                </Card> : null}
        </Box>
    );
};

const Transitions = (props: { transitions: Transition[] | undefined, language: string | undefined }) => {
    const {transitions, language} = props;
    const t = useTranslate();
    const refresh = useRefresh();

    if (!transitions || transitions.length === 0) {
        return null;
    }

    return (
        <ButtonGroup variant="contained" aria-label="" fullWidth={true}>
            {transitions.map((transition) => {
                    const status = returnStates[transition.target];
                    let button;

                    switch (transition.id) {
                        case 'declineRequest':
                            button = (
                                <DeclineRequestDialog transition={transition} language={language}/>
                            );
                            break;
                        case 'clarificationRequest':
                            button = (
                                <ClarificationRequestDialog transition={transition} language={language}/>
                            );
                            break;
                        case 'packageLost':
                            button = (
                                <PackageLostRequestDialog transition={transition} language={language}/>
                            );
                            break;
                        default:
                            button = (
                                <DefaultRequestDialog transition={transition} language={language}/>
                            );
                            break;
                    }

                    return button;
                }
            )}
        </ButtonGroup>
    );
};
const StatusPanel = (props: { record: OrderReturn }) => {
    const record = props.record;
    const t = useTranslate();

    const {data: transitions, isLoading} = useGetList<Transition>(`order-returns/${record.id}/transitions`);

    if (isLoading) {
        return <Loading/>;
    }

    const acceptHoldOn = !record?.labelExists;

    const requestedStatus = returnStates["requested"];
    const status = returnStates[record.status];

    return (
        <Box width={{xs: "100%", md: 300, lg: 400}} ml={{md: 0}} mt={{xs: 2, md: 0}} sx={{minWidth: 300}}>
            <Card>
                <CardContent sx={{width: "100%"}}>
                    <Labeled
                        source="status"
                        label={`resources.order.return.currentStatus`}
                        sx={{textAlign: "center", width: "100%"}}
                    >
                        <Typography variant="h6" gutterBottom>
                            {t(status.translationKey)}
                        </Typography>
                    </Labeled>
                    {(status.id === requestedStatus.id && acceptHoldOn) && (
                        <Typography variant="caption" color="text.secondary">
                            {t('resources.order.return.requestedOn')}
                        </Typography>
                    )}
                </CardContent>
                <CardActions sx={{display: transitions?.length ? 'flex' : 'none'}}>
                    <Transitions transitions={transitions} language={record?.order?.locale}/>
                </CardActions>
            </Card>
        </Box>
    );
};

export const OrderReturnShow = () => {
    const isSmall = useMediaQuery<Theme>(theme => theme.breakpoints.down('md'));
    const t = useTranslate();

    return (
        <>
            <Show
                aside={<Aside/>}
                sx={{
                    '& .RaShow-main': {display: isSmall ? 'block' : 'flex'},
                }}
            >
                <CustomHelmet/>
                <Box sx={{display: "flex", gap: 4, flexWrap: "wrap", flexDirection: "column"}}>
                    <OrderReturnDetails/>
                </Box>
            </Show>
            <Show
                sx={{
                    '& .RaShow-main': {display: isSmall ? 'block' : 'flex'},
                }}
            >
                <Box sx={{display: "flex", gap: 4, flexWrap: "wrap"}}>
                    <OrderReturnItems/>
                </Box>
            </Show>
            <Show
                sx={{
                    '& .RaShow-main': {display: isSmall ? 'block' : 'flex'},
                }}
            >
                <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                    <OrderReturnLabels/>
                </Box>
            </Show>
            <Show
                sx={{
                    '& .RaShow-main': {display: isSmall ? 'block' : 'flex'},
                }}
            >
                <Box sx={{display: "flex", gap: 4, flexWrap: "wrap"}}>
                    <OrderShow/>
                </Box>
            </Show>

        </>
    )
};
